import { ReactNode, useEffect, useRef, useState } from "react"
import OverlayTrigger from "@rio-cloud/rio-uikit/OverlayTrigger"
import Popover from "@rio-cloud/rio-uikit/Popover"
import Button from "@rio-cloud/rio-uikit/Button"
import useClipboard from "@rio-cloud/rio-uikit/useClipboard"
import { useIntl } from "react-intl"

export const OverflowClipboardCell = (props: {
    cellId: string
    title: ReactNode
    value: string
    children?: ReactNode
    popoverContent?: ReactNode
}): ReactNode => {
    const { cellId, title, value, children } = props
    const clipboard = useClipboard()
    const copyIconResetTimeoutRef = useRef<number | undefined>(undefined)
    const contentRef = useRef<HTMLDivElement>(null)
    const [isOverflowing, setIsOverflowing] = useState(false)
    const [copied, setCopied] = useState(false)

    useEffect(() => {
        const updateOverflowState = () => {
            const element = contentRef.current
            if (element === null) {
                setIsOverflowing(false)
                return
            }

            const measurableElements = [...element.querySelectorAll<HTMLElement>("[data-overflow-copy-line='true']")]
            if (measurableElements.length === 0) {
                setIsOverflowing(
                    element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight
                )
                return
            }

            setIsOverflowing(
                measurableElements.some(
                    measurableElement =>
                        measurableElement.scrollWidth > measurableElement.clientWidth
                        || measurableElement.scrollHeight > measurableElement.clientHeight
                )
            )
        }

        updateOverflowState()
        const contentElement = contentRef.current
        const resizeObserver = typeof ResizeObserver !== "undefined"
            ? new ResizeObserver(updateOverflowState)
            : undefined

        if (contentElement !== null) {
            resizeObserver?.observe(contentElement)
        }
        window.addEventListener("resize", updateOverflowState)

        return () => {
            resizeObserver?.disconnect()
            window.removeEventListener("resize", updateOverflowState)
        }
    }, [value])

    useEffect(() => {
        return () => {
            if (copyIconResetTimeoutRef.current !== undefined) {
                window.clearTimeout(copyIconResetTimeoutRef.current)
            }
        }
    }, [])

    const intl = useIntl()

    const showCopiedIconTemporarily = () => {
        setCopied(true)
        if (copyIconResetTimeoutRef.current !== undefined) {
            window.clearTimeout(copyIconResetTimeoutRef.current)
        }
        copyIconResetTimeoutRef.current = window.setTimeout(() => {
            setCopied(false)
            copyIconResetTimeoutRef.current = undefined
        }, 2000)
    }

    const popoverContent = props.popoverContent ?? (value.includes("\n")
        ? (
            <div className="display-flex flex-column">
                {value.split("\n").map(line => <span key={line}>{line}</span>)}
            </div>
        )
        : value)

    const content = (
        <div className="display-flex flex-row width-100pct" ref={contentRef} style={{ minWidth: 0 }}>
            {children ?? (
                <span
                    className="text-normal ellipsis-1"
                    title={value || undefined}
                    data-testid={`${cellId}-value`}
                    data-overflow-copy-line="true"
                >
                    {value}
                </span>
            )}
        </div>
    )

    if (value === "" || !isOverflowing) {
        return content
    }

    return (
        <OverlayTrigger
            trigger="click"
            placement="bottom"
            overlay={
                <Popover
                    id={`${cellId}-popover`}
                    titleClassName="padding-10"
                    contentClassName="padding-10"
                    className="margin-top-10"
                >
                    <Popover.Title className="display-flex justify-content-between align-items-start padding-10">
                        <span className="text-medium">{title}</span>
                        <Button
                            bsStyle="muted"
                            bsSize="xs"
                            className="hover-bg-none"
                            iconName={copied ? "rioglyph-ok-sign" : "rioglyph-duplicate"}
                            iconOnly
                            title={intl.formatMessage({ id: "common.copyToClipboard" })}
                            aria-label={intl.formatMessage({ id: "common.copyToClipboard" })}
                            onClick={event => {
                                event.stopPropagation()
                                clipboard.copy(value)
                                showCopiedIconTemporarily()
                            }}
                        />
                    </Popover.Title>
                    <Popover.Content className="padding-10 word-break">
                        {popoverContent}
                    </Popover.Content>
                </Popover>
            }
        >
            <div
                className="display-flex flex-row cursor-pointer width-100pct"
                data-testid={`${cellId}-trigger`}
                style={{ minWidth: 0 }}
            >
                {content}
            </div>
        </OverlayTrigger>
    )
}
